import { useCallback, useEffect, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import moment from 'moment'
import Swal from 'sweetalert2'
import AvailabilityService from '@/services/AvailabilityService'
import { sortAvailabilitySlots } from '../availabilitySlots'
import {
	availabilityToFormState,
	buildEmptyFormState,
	isValidDateRange,
	toDateInputValue,
} from '../availabilityUtils'

export default function useDyspozycjaForm(mode) {
	const isEdit = mode === 'edit'
	const { t } = useTranslation()
	const navigate = useNavigate()
	const location = useLocation()
	const { id } = useParams()

	const anchorDate = toDateInputValue(location.state?.anchorDate || moment().format('YYYY-MM-DD'))

	const [availability, setAvailability] = useState(null)
	const [formState, setFormState] = useState(() => buildEmptyFormState(isEdit ? '' : anchorDate))
	const [formError, setFormError] = useState('')
	const [saving, setSaving] = useState(false)
	const [loading, setLoading] = useState(isEdit)
	const [loadError, setLoadError] = useState('')
	const [hasExistingEntryForRange, setHasExistingEntryForRange] = useState(false)
	const [rangeEntriesError, setRangeEntriesError] = useState('')

	useEffect(() => {
		if (!isEdit) return

		let cancelled = false

		const load = async () => {
			setLoading(true)
			setLoadError('')

			const result = await AvailabilityService.getById(id)
			if (cancelled) return

			if (result.error) {
				setAvailability(null)
				setLoadError(result.error)
			} else {
				const nextState = availabilityToFormState(result.data)
				nextState.dateTo = nextState.dateFrom
				setAvailability(result.data)
				setFormState(nextState)
			}
			setLoading(false)
		}

		load()
		return () => {
			cancelled = true
		}
	}, [id, isEdit])

	useEffect(() => {
		const { dateFrom, dateTo } = formState
		if (!isValidDateRange(dateFrom, dateTo)) {
			setHasExistingEntryForRange(false)
			setRangeEntriesError('')
			return
		}

		let cancelled = false

		const checkRange = async () => {
			const result = await AvailabilityService.getMyAvailability({
				startDate: dateFrom,
				endDate: dateTo,
				limit: 400,
				sortColumn: 'startDate',
				sortDirection: 'asc',
			})
			if (cancelled) return

			if (result.error) {
				setHasExistingEntryForRange(false)
				setRangeEntriesError(result.error)
				return
			}

			const items = Array.isArray(result.data) ? result.data : []
			const conflicting = isEdit
				? items.filter(item => String(item?._id) !== String(id))
				: items

			setHasExistingEntryForRange(conflicting.length > 0)
			setRangeEntriesError('')
		}

		checkRange()
		return () => {
			cancelled = true
		}
	}, [formState.dateFrom, formState.dateTo, id, isEdit])

	const handleBack = useCallback(() => {
		navigate('/centrumSzkolen/dyspozycja')
	}, [navigate])

	const handleCancel = useCallback(() => {
		navigate(`/centrumSzkolen/dyspozycja/details/${id}`)
	}, [id, navigate])

	const handleFieldChange = useCallback((field, value) => {
		setFormState(prev => {
			if (field === 'dateFrom') {
				if (isEdit) {
					return { ...prev, dateFrom: value, dateTo: value }
				}
				const nextDateTo = prev.dateTo && moment(prev.dateTo, 'YYYY-MM-DD', true).isBefore(moment(value, 'YYYY-MM-DD', true), 'day')
					? value
					: prev.dateTo
				return { ...prev, dateFrom: value, dateTo: nextDateTo || value }
			}

			if (field === 'dateTo') {
				return { ...prev, dateTo: value }
			}

			return { ...prev, [field]: value }
		})
	}, [isEdit])

	const handleSlotToggle = useCallback(slotId => {
		setFormState(prev => {
			const nextSlots = prev.slots.includes(slotId)
				? prev.slots.filter(item => item !== slotId)
				: [...prev.slots, slotId]
			return { ...prev, slots: sortAvailabilitySlots(nextSlots) }
		})
	}, [])

	const validateForm = useCallback(() => {
		const messages = isEdit
			? t('trainingAvailability.edit.messages', { returnObjects: true })
			: t('trainingAvailability.add.messages', { returnObjects: true })

		if (!formState.dateFrom) return messages.dateRequired
		if (!formState.dateTo) return messages.dateToRequired
		if (!isValidDateRange(formState.dateFrom, formState.dateTo)) return messages.invalidDateRange
		if (formState.slots.length === 0) return messages.slotsRequired
		if (hasExistingEntryForRange) return messages.duplicateDateRange
		return ''
	}, [formState, hasExistingEntryForRange, isEdit, t])

	const handleSubmit = useCallback(async e => {
		e?.preventDefault()
		setFormError('')

		const validationError = validateForm()
		if (validationError) {
			setFormError(validationError)
			return
		}

		setSaving(true)
		const payload = {
			startDate: formState.dateFrom,
			endDate: isEdit ? formState.dateFrom : formState.dateTo,
			slots: formState.slots,
			notes: formState.notes,
		}

		const result = isEdit
			? await AvailabilityService.requestChange(id, 'edit', payload)
			: await AvailabilityService.create(payload)

		setSaving(false)

		if (!result.success) {
			const failKey = isEdit ? 'requestFailed' : 'createFailed'
			const messages = isEdit
				? t('trainingAvailability.edit.messages', { returnObjects: true })
				: t('trainingAvailability.add.messages', { returnObjects: true })
			setFormError(result.error || messages[failKey])
			return
		}

		if (isEdit) {
			await Swal.fire({
				title: t('notifications.successTitle'),
				text: t('trainingAvailability.edit.messages.requestSent'),
				icon: 'success',
			})
		}

		navigate('/centrumSzkolen/dyspozycja')
	}, [formState, id, isEdit, navigate, validateForm, t])

	return {
		availability,
		formError,
		formState,
		handleBack,
		handleCancel,
		handleCreateSubmit: handleSubmit,
		handleEditSubmit: handleSubmit,
		handleFieldChange,
		handleSlotToggle,
		hasExistingEntryForRange,
		loadError,
		loading,
		rangeEntriesError,
		saving,
	}
}
